import { DateTimeOffset } from 'components/Form/BDatePicker';
import { SupportedDemandType } from 'enums/SupportedDemandType';
import { useMemo } from 'react';
import { dateService } from 'services/DateService';
import { VehicleBasedDemandState } from 'types/demand.type';
import { HourlyRentalInfo } from 'types/vehicle.type';
import { usePriceWidgetIsSpecialDate } from './usePriceWidgetIsSpecialDate';

export function usePriceWidgetDisabledHours(
  state: VehicleBasedDemandState,
  supportedDemandTypes: SupportedDemandType[],
  rentalInfo: HourlyRentalInfo
) {
  const dayjs = dateService.getDayjs();
  const { getIsSpecialDate } = usePriceWidgetIsSpecialDate(rentalInfo);

  const calculateDisabledHoursByDate = (
    date: Date | null
  ): DateTimeOffset[] => {
    if (
      supportedDemandTypes.includes(
        SupportedDemandType.IMMEDIATE_RESERVATION
      ) &&
      rentalInfo.operationalHours &&
      rentalInfo.operationalHours.end !== rentalInfo.operationalHours.start &&
      date
    ) {
      const resultArray: DateTimeOffset[] = [];
      const isSpecialDate = getIsSpecialDate(date);
      const rentalDurationOffset =
        (isSpecialDate
          ? rentalInfo.minimumRentDurationOnSpecialDays
          : rentalInfo.minimumRentDuration) - 1;
      const nextDate = dayjs(date).add(1, 'day');
      const isNextDateSpecialDate = getIsSpecialDate(nextDate.toDate());
      const nextDateRentalDurationOffset =
        (isNextDateSpecialDate
          ? rentalInfo.minimumRentDurationOnSpecialDays
          : rentalInfo.minimumRentDuration) - 1;
      const opHours = rentalInfo.operationalHours;
      const offsetTime =
        opHours.end < nextDateRentalDurationOffset
          ? Math.max(rentalDurationOffset, nextDateRentalDurationOffset)
          : rentalDurationOffset;
      const offsetedEndTime = opHours.end - offsetTime;
      const endTime =
        offsetedEndTime < 0 ? 24 + offsetedEndTime : offsetedEndTime;

      const isOffset = (hour: number) => {
        if (endTime <= opHours.end) {
          return hour >= endTime && hour < opHours.end;
        }
        return hour >= endTime || hour < opHours.end;
      };

      if (endTime < opHours.start) {
        [...Array(24)].forEach((_, idx) => {
          if (!(idx >= opHours.start || idx < endTime)) {
            resultArray.push({
              hour: idx,
              isOffset: isOffset(idx),
            });
          }
        });
      } else {
        [...Array(24)].forEach((_, idx) => {
          if (idx < opHours.start || idx >= endTime) {
            resultArray.push({
              hour: idx,
              isOffset: isOffset(idx),
            });
          }
        });
      }
      return resultArray;
    }
    return [];
  };

  const datePickerDisabledHours: DateTimeOffset[] = useMemo(() => {
    return calculateDisabledHoursByDate(state.date);
  }, [rentalInfo.operationalHours, supportedDemandTypes, state.date]);

  return {
    datePickerDisabledHours,
  };
}
